import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';
import { useLocation } from 'react-use';
import WebApp from '@twa-dev/sdk';
import { QueryClient, QueryClientProvider } from 'react-query';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { AppProvider, useApp } from '../contexts/AppContext';
import { handleResize, shouldBlockDesktop } from '../lib/utils';
import Popups from '../components/Popups';
import ForcePortrait from '../components/ForcePortrait';
import { env } from '../lib/env';
import { Notifications } from '../components/Notifications';
import { onlineStatusInit } from '../controllers/OnlineStatusController';
import ImagePreloader from '../components/ImagePreloader';
import BlockDesktop from '../components/BlockDesktop';
import Friends from '../pages/Friends';
import FriendsNote from '../pages/FriendsNote';

const ScaledApp = styled.div`
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  width: 1080px;
  height: 0;
  margin: 0 auto;

  @media (max-width: 1079px) {
    transform-origin: 0 0;
    transform: scale(var(--scale));
  }

  .fade-enter {
    position: absolute;
    opacity: 0;
    z-index: 10;
    transition: opacity 300ms ease-in;
  }

  .fade-enter.fade-enter-active {
    opacity: 1;
    transition: opacity 300ms ease-in;
  }
`;

const VerticalApp = styled.div`
  @media screen and (orientation: landscape) {
    display: none;
  }
`;

Sentry.init({
  dsn: env.sentryDsn,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 0.05, //  Capture 100% of the transactions
  tracePropagationTargets: ['localhost'],
  release: env.build || 'unknown',
});

const queryClient = new QueryClient();

const Providers = ({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={queryClient}>
    <AppProvider>{children}</AppProvider>
  </QueryClientProvider>
);

const HideWhileLoading = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useApp();

  if (loading) return null;
  return children;
};

const PreloadImages = () => {
  const popupImages = ['/img/Meerkat.png'];
  return <ImagePreloader images={[...popupImages]} />;
};

const HandleBack = () => {
  const location = useLocation();
  const navigate = useNavigate();

  WebApp.BackButton.onClick(() => {
    navigate('/');
  });

  if (location.pathname === '/') WebApp.BackButton.hide();
  else WebApp.BackButton.show();

  return null;
};

export function App() {
  useEffect(() => {
    handleResize();
    onlineStatusInit();
  }, []);

  const location = useLocation();

  if (shouldBlockDesktop()) {
    document.getElementById('preloader')?.remove();
    return <BlockDesktop />;
  } else {
    WebApp.BackButton.show();
  }

  return (
    <Providers>
      <Router>
        <Notifications />
        <ForcePortrait />
        <VerticalApp>
          <PreloadImages />
          <HandleBack />
          <HideWhileLoading>
            <Popups />
            <ScaledApp>
              <TransitionGroup>
                <CSSTransition key={location.pathname} classNames="fade" timeout={300}>
                  <Routes location={location}>
                    <Route path="/" element={<Friends />} />
                    <Route path="/friends/note" element={<FriendsNote />} />
                  </Routes>
                </CSSTransition>
              </TransitionGroup>
            </ScaledApp>
          </HideWhileLoading>
        </VerticalApp>
      </Router>
    </Providers>
  );
}

export default App;
